import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';

const BROADCAST_API_BASE = `${API_URL}/v1`; // WhatsappBroadcastController is mounted at /api/v1/broadcasts in C#; email uses /api/EmailBroadcast elsewhere

export interface Broadcast {
  id: number;
  sector_id: number;
  name: string;
  status: string;
  total_count: number;
  sent_count: number;
  failed_count: number;
  created_by: number;
  use_active_ai: boolean;
  /** When true (default), each send enrolls the contact for campaign AI; false forces human after each send. */
  ai_reply_enabled: boolean;
  ai_agent_id?: number | null;
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
  recurrence_type?: number;
  recurrence_interval?: number;
  recurrence_end_date?: string;
  recurrence_weekdays?: string;
  recurrence_month_days?: string;
  exclude_weekends?: boolean;
  exclude_holidays?: boolean;
  scheduled_time?: string;
  timezone?: string;
  next_run_at?: string;
  /** Next humanized send window (period + local times + TZ), from API. */
  human_next_window?: string;
  execution_count?: number;
  last_execution_at?: string;
  paused_by_daily_limit?: boolean;
  paused_by_time_window?: boolean;
  /** Set when the campaign was created from an imported spreadsheet list (not group/CRM picker). */
  source_import_list_id?: number | null;
  /** True when created with materialize_contacts_on_send (import file + campaign). Hides group/recipient pickers in the UI. */
  audience_from_import?: boolean;
}

export type BroadcastActionResult = { ok: true } | { ok: false; message: string };

export interface BroadcastExecution {
  id: number;
  broadcast_id: number;
  started_at: string;
  completed_at?: string;
  status: string;
  sent_count: number;
  failed_count: number;
  received_count: number;
}

export interface BroadcastMessage {
  id: number;
  broadcast_id: number;
  content: string;
  order_index: number;
  media_type?: 'text' | 'audio';
  media_url?: string | null;
  media_mime_type?: string | null;
}

export interface BroadcastRecipient {
  id: number;
  broadcast_id: number;
  contact_id: number;
  /** Present for spreadsheet / defer-persist import rows (links to broadcast_import_list_items). */
  list_item_id?: number | null;
  contact_name: string;
  number: string;
  status: string;
  message_used?: string;
  sent_at?: string;
  error?: string;
}

export interface BroadcastSummary {
  broadcast: Broadcast;
  messages: BroadcastMessage[];
  recipients: BroadcastRecipient[];
  executions?: BroadcastExecution[];
  execution_count?: number;
}

export interface LIDValidationEntry {
  recipient_row_id: number;
  contact_id: number;
  contact_name: string;
  input: string;
  parsed_jid: string;
  kind: string;
  ok: boolean;
  resolved_lid?: string;
  steps: string[];
  jid_ok?: boolean;
  whatsapp_registered?: boolean | null;
  lid_in_store?: boolean;
  lid_migration_on?: boolean;
  send_hint?: string;
  input_shape?: string;
  address_type?: string;
  primary_send_jid?: string;
  preferred_send_jid?: string;
  contact_origin?: string;
  contact_origin_detail?: string;
}

export interface LIDValidationReport {
  broadcast_id: number;
  sector_id: number;
  scope: string;
  total: number;
  ok_count: number;
  fail_count: number;
  skipped_count: number;
  client_ready: boolean;
  error?: string;
  pending_in_db: number;
  entries: LIDValidationEntry[];
  seconds_elapsed: number;
}

export interface BroadcastMessageVariantInput {
  media_type: 'text' | 'audio';
  content: string;
  media_url?: string;
  media_mime_type?: string;
  audio_base64?: string;
}

export interface CreateBroadcastRequest {
  sector_id: number;
  name: string;
  group_id?: number;
  contact_ids: number[];
  contacts?: { id: number; name: string; number: string }[];
  /** @deprecated Prefer message_variants */
  messages?: string[];
  message_variants?: BroadcastMessageVariantInput[];
  use_active_ai?: boolean;
  /** When true (default), each send enrolls the contact for campaign AI; when false, human handling. */
  ai_reply_enabled?: boolean;
  ai_agent_id?: number | null;
  /** 0 = one-time (manual start; no auto recurrence). 1–3 = daily / weekly / monthly. */
  recurrence_type?: number;
  recurrence_interval?: number;
  recurrence_end_date?: string;
  recurrence_weekdays?: string;
  recurrence_month_days?: string;
  exclude_weekends?: boolean;
  exclude_holidays?: boolean;
  scheduled_time?: string;
  timezone?: string;
  /** When true, new numbers use broadcast_import_lists; CRM contact rows are created only after a successful send. */
  materialize_contacts_on_send?: boolean;
  import_list_name?: string;
}

const getAuthHeaders = () => {
  const token = SessionService.getToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

function asBool(v: unknown): boolean {
  return v === true || v === 'true' || v === 1 || v === '1';
}

function normalizeAiAgentId(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function normalizeBroadcast(raw: unknown): Broadcast {
  if (!raw || typeof raw !== 'object') return raw as Broadcast;
  const r = raw as Record<string, unknown>;
  const base = { ...r } as unknown as Broadcast;
  const useActiveRaw = r.use_active_ai ?? r.useActiveAI;
  const agentRaw = r.ai_agent_id ?? r.aiAgentId;
  const daily = r.paused_by_daily_limit ?? r.pausedByDailyLimit;
  const tw = r.paused_by_time_window ?? r.pausedByTimeWindow;
  const lastExec = r.last_execution_at ?? r.lastExecutionAt;
  const st = r.status ?? r.Status;
  const statusNorm =
    typeof st === 'string' && st.trim() !== '' ? String(st).trim().toLowerCase() : undefined;
  const srcList = r.source_import_list_id ?? r.sourceImportListId;
  const sourceImportListId =
    typeof srcList === 'number' && Number.isFinite(srcList)
      ? srcList
      : srcList != null && srcList !== ''
        ? Number(srcList)
        : null;
  const afi = r.audience_from_import ?? r.audienceFromImport;
  const air = r.ai_reply_enabled ?? r.aiReplyEnabled;
  return {
    ...base,
    ...(statusNorm ? { status: statusNorm } : {}),
    use_active_ai: asBool(useActiveRaw),
    ai_reply_enabled: air === false || air === 'false' || air === 0 ? false : true,
    ai_agent_id: normalizeAiAgentId(agentRaw),
    paused_by_daily_limit: asBool(daily),
    paused_by_time_window: asBool(tw),
    ...(typeof lastExec === 'string' && lastExec ? { last_execution_at: lastExec } : {}),
    ...(sourceImportListId != null && !Number.isNaN(sourceImportListId) && sourceImportListId > 0
      ? { source_import_list_id: sourceImportListId }
      : {}),
    ...(asBool(afi) ? { audience_from_import: true } : {}),
  };
}

function isNextRunInFuture(b: Pick<Broadcast, 'next_run_at'>): boolean {
  if (!b.next_run_at) return false;
  const t = new Date(b.next_run_at).getTime();
  return Number.isFinite(t) && t > Date.now();
}

export function isBroadcastAutoPaused(
  b: Pick<Broadcast, 'status' | 'paused_by_daily_limit' | 'paused_by_time_window' | 'next_run_at'>
): boolean {
  if (b.status === 'paused' && (!!b.paused_by_daily_limit || !!b.paused_by_time_window)) return true;
  if (b.status === 'pending' && !!b.paused_by_daily_limit && isNextRunInFuture(b)) return true;
  return false;
}

/** True while sends are in progress: actively running, or auto-paused (daily cap / send window). Editing is disabled in these states. */
export function isBroadcastEditLocked(
  b: Pick<Broadcast, 'status' | 'paused_by_daily_limit' | 'paused_by_time_window' | 'next_run_at'>
): boolean {
  const st = String(b.status || '').toLowerCase();
  if (st === 'running') return true;
  if (isBroadcastAutoPaused(b)) return true;
  return false;
}

export interface BroadcastListResponse {
  broadcasts: Broadcast[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export const listBroadcasts = async (
  sectorId: number,
  options?: { status?: string; limit?: number; offset?: number }
): Promise<BroadcastListResponse> => {
  try {
    const params = new URLSearchParams({ sector_id: String(sectorId) });
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.offset !== undefined) params.append('offset', String(options.offset));

    const response = await axios.get(`${BROADCAST_API_BASE}/broadcasts?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    const rows = response.data?.data;
    const list = Array.isArray(rows) ? rows.map((row: unknown) => normalizeBroadcast(row)) : [];
    return {
      broadcasts: list,
      pagination: response.data?.pagination || { total: 0, limit: 20, offset: 0 },
    };
  } catch (error) {
    console.error('Error listing broadcasts:', error);
    return { broadcasts: [], pagination: { total: 0, limit: 20, offset: 0 } };
  }
};

export const validateBroadcastLids = async (
  broadcastId: number,
  scope: 'pending' | 'all' = 'pending'
): Promise<LIDValidationReport> => {
  const response = await axios.post<{ data?: LIDValidationReport; message?: string }>(
    `${BROADCAST_API_BASE}/broadcasts/${broadcastId}/validate-lids`,
    {},
    { params: { scope }, headers: getAuthHeaders() }
  );
  const payload = response.data?.data;
  if (!payload) throw new Error(response.data?.message || 'validate-lids failed');
  return payload;
};

function normalizeBroadcastRecipient(raw: unknown): BroadcastRecipient | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id ?? r.Id);
  const broadcastId = Number(r.broadcast_id ?? r.broadcastId);
  const contactId = Number(r.contact_id ?? r.contactId);
  if (!Number.isFinite(id) || !Number.isFinite(broadcastId)) return null;
  const li = r.list_item_id ?? r.listItemId;
  const listItemId = typeof li === 'number' && Number.isFinite(li) ? li : li != null && li !== '' ? Number(li) : null;
  return {
    id,
    broadcast_id: broadcastId,
    contact_id: Number.isFinite(contactId) ? contactId : 0,
    ...(listItemId != null && !Number.isNaN(listItemId) && listItemId > 0 ? { list_item_id: listItemId } : {}),
    contact_name: String(r.contact_name ?? r.contactName ?? ''),
    number: String(r.number ?? ''),
    status: String(r.status ?? ''),
    ...(typeof r.message_used === 'string' ? { message_used: r.message_used } : {}),
    ...(typeof r.sent_at === 'string' ? { sent_at: r.sent_at } : {}),
    ...(typeof r.error === 'string' ? { error: r.error } : {}),
  };
}

export const getBroadcast = async (id: number): Promise<BroadcastSummary | null> => {
  try {
    const response = await axios.get(`${BROADCAST_API_BASE}/broadcasts/${id}`, {
      headers: getAuthHeaders(),
    });
    const data = response.data?.data || null;
    if (data?.broadcast) {
      const recRaw = data.recipients;
      const recipients = Array.isArray(recRaw)
        ? recRaw
            .map((row: unknown) => normalizeBroadcastRecipient(row))
            .filter((x): x is BroadcastRecipient => x != null)
        : data.recipients;
      return { ...data, broadcast: normalizeBroadcast(data.broadcast), recipients };
    }
    return data;
  } catch (error) {
    console.error('Error getting broadcast:', error);
    return null;
  }
};

export const createBroadcast = async (data: CreateBroadcastRequest): Promise<Broadcast | null> => {
  try {
    const response = await axios.post(`${BROADCAST_API_BASE}/broadcasts`, data, {
      headers: getAuthHeaders(),
    });
    return response.data?.data || null;
  } catch (error) {
    console.error('Error creating broadcast:', error);
    throw error;
  }
};

export interface UpdateBroadcastRequest {
  name?: string;
  /** @deprecated Prefer message_variants */
  messages?: string[];
  message_variants?: BroadcastMessageVariantInput[];
  contact_ids?: number[];
  contacts?: { id: number; name: string; number: string }[];
  recurrence_type?: number;
  recurrence_interval?: number;
  recurrence_end_date?: string;
  recurrence_weekdays?: string;
  recurrence_month_days?: string;
  exclude_weekends?: boolean;
  exclude_holidays?: boolean;
  scheduled_time?: string;
  timezone?: string;
  ai_agent_id?: number | null;
  /** When false, each campaign send forces human handling (no AI on replies). */
  ai_reply_enabled?: boolean;
  /** When set without changing ai_agent_id, toggles sector-level broadcast AI (no campaign agent row). */
  use_active_ai?: boolean;
}

export const updateBroadcast = async (id: number, data: UpdateBroadcastRequest): Promise<Broadcast | null> => {
  try {
    const response = await axios.put(`${BROADCAST_API_BASE}/broadcasts/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data?.data || null;
  } catch (error) {
    console.error('Error updating broadcast:', error);
    throw error;
  }
};

function broadcastErrorMessage(error: unknown, fallback: string): string {
  const ax = error as { response?: { data?: { message?: string } } };
  return ax?.response?.data?.message ?? fallback;
}

export const startBroadcast = async (id: number): Promise<BroadcastActionResult> => {
  try {
    await axios.post(`${BROADCAST_API_BASE}/broadcasts/${id}/start`, {}, {
      headers: getAuthHeaders(),
    });
    return { ok: true };
  } catch (error) {
    console.error('Error starting broadcast:', error);
    return { ok: false, message: broadcastErrorMessage(error, 'Could not start the broadcast.') };
  }
};

/** Queues ProcessBroadcast with force_validation: ignores 7:00–21:59 window and daily cap (WhatsApp service worker). */
export const startBroadcastWorkerTest = async (id: number): Promise<BroadcastActionResult> => {
  try {
    await axios.post(`${BROADCAST_API_BASE}/broadcasts/${id}/start-for-test`, {}, {
      headers: getAuthHeaders(),
    });
    return { ok: true };
  } catch (error) {
    console.error('Error starting broadcast (test worker):', error);
    return { ok: false, message: broadcastErrorMessage(error, 'Could not queue the test worker run.') };
  }
};

export const cancelBroadcast = async (id: number): Promise<boolean> => {
  try {
    await axios.post(`${BROADCAST_API_BASE}/broadcasts/${id}/cancel`, {}, {
      headers: getAuthHeaders(),
    });
    return true;
  } catch (error) {
    console.error('Error cancelling broadcast:', error);
    return false;
  }
};

export const pauseBroadcast = async (id: number): Promise<boolean> => {
  try {
    await axios.post(`${BROADCAST_API_BASE}/broadcasts/${id}/pause`, {}, {
      headers: getAuthHeaders(),
    });
    return true;
  } catch (error) {
    console.error('Error pausing broadcast:', error);
    return false;
  }
};

export const restartBroadcast = async (id: number): Promise<BroadcastActionResult> => {
  try {
    await axios.post(`${BROADCAST_API_BASE}/broadcasts/${id}/restart`, {}, {
      headers: getAuthHeaders(),
    });
    return { ok: true };
  } catch (error) {
    console.error('Error restarting broadcast:', error);
    return { ok: false, message: broadcastErrorMessage(error, 'Could not re-run the broadcast.') };
  }
};

/** Re-run a stopped or finished campaign (reset pending/failed, then start sending). */
export const rerunBroadcast = async (id: number): Promise<BroadcastActionResult> => {
  try {
    await axios.post(`${BROADCAST_API_BASE}/broadcasts/${id}/rerun`, {}, {
      headers: getAuthHeaders(),
    });
    return { ok: true };
  } catch (error) {
    console.error('Error re-running broadcast:', error);
    return { ok: false, message: broadcastErrorMessage(error, 'Could not re-run the broadcast.') };
  }
};

export const deleteBroadcast = async (id: number): Promise<boolean> => {
  try {
    await axios.delete(`${BROADCAST_API_BASE}/broadcasts/${id}`, {
      headers: getAuthHeaders(),
    });
    return true;
  } catch (error) {
    console.error('Error deleting broadcast:', error);
    return false;
  }
};
